import Base from '@studiometa/js-toolkit';
import { isDev } from '../../config';
import ItemSecondLevel from './ItemSecondLevel';

/**
 * ItemFirstLevel class.
 */
export default class ItemFirstLevel extends Base {
  /**
   * Class config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'ItemFirstLevel-Desktop',
      log: isDev(),
      components: {
        ItemSecondLevel,
      },
    };
  }

  /**
   * Get a prefixed ID for the submenu.
   * @return {String}
   */
  get submenuId() {
    return `submenu-${this.$id}`;
  }

  /**
   * Test if the current instance has a submenu.
   * @return {Boolean}
   */
  get hasSubmenu() {
    return !!this.$refs.submenu;
  }

  /**
   * Update position when mouting the component.
   * @return {void}
   */
  mounted() {
    this.submenuIsOpen = false;

    if (this.hasSubmenu) {
      this.$refs.link.setAttribute('aria-controls', this.submenuId);
      this.$refs.link.setAttribute('aria-expanded', 'false');
      this.$refs.link.setAttribute('aria-haspopup', 'true');
      this.$refs.submenu.setAttribute('id', this.submenuId);
    }

    const unbindMethods = [];

    if (this.$parent) {
      unbindMethods.push(this.$parent.$on('hide', this.closeCurrentSubmenu));
    }

    if (this.$children.ItemSecondLevel) {
      this.unbindMethods = [
        ...unbindMethods,
        ...this.$children.ItemSecondLevel.map((item, index) => [
          item.$on('closeSubmenu', () => this.onItemsCloseSubmenu(index)),
          item.$on('openSubmenu', () => this.onItemsOpenSubmenu(index)),
        ]),
      ].flat();
    } else {
      this.unbindMethods = [...unbindMethods].flat();
    }
  }

  /**
   * Delete the `__base__` reference on destroy to allow the mobile version to mount.
   */
  destroyed() {
    this.unbindMethods.forEach((unbindMethod) => unbindMethod());
    // eslint-disable-next-line no-underscore-dangle
    delete this.$el.__base__;
  }

  /**
   * Manage keyboard usage of the submenu.
   *
   * @param  {Boolean} options.isDown If the event is `keydown`.
   * @param  {Boolean} options.ESC    If the key pressed is the escape key.
   * @return {void}
   */
  keyed({ isDown, ESC }) {
    if (isDown && ESC) {
      this.closeSubmenu();
    }
  }

  /**
   * Open the submenu on focus.
   */
  onFocusin() {
    this.$emit('focusin');
    this.openSubmenu();
  }

  /**
   * Open submenu on mouseenter.
   */
  onMouseenter() {
    this.openSubmenu();
  }

  /**
   * Close the submenu on mouseleave.
   */
  onMouseleave() {
    this.closeSubmenu();
  }

  /**
   * Open the submenu.
   */
  openSubmenu() {
    if (!this.hasSubmenu || this.submenuIsOpen) {
      return;
    }

    this.$emit('openSubmenu');

    this.loadPushImage();

    this.submenuIsOpen = true;
    this.$refs.link.setAttribute('aria-expanded', 'true');
    this.$refs.border.classList.remove('translate-y-full');
    this.$refs.submenu.setAttribute('aria-hidden', 'false');
    this.$refs.submenu.classList.remove('hidden', 'pointer-events-none');

    this.$children?.ItemSecondLevel?.[0]?.openSubmenu();
  }

  /**
   * Close the submenu.
   * @return {void}
   */
  closeSubmenu() {
    if (!this.hasSubmenu || !this.submenuIsOpen) {
      return;
    }

    this.$emit('closeSubmenu');

    this.submenuIsOpen = false;
    this.$refs.link.setAttribute('aria-expanded', 'false');
    this.$refs.submenu.setAttribute('aria-hidden', 'true');
    this.$refs.border.classList.add('translate-y-full');
    this.$refs.submenu.classList.add('hidden', 'pointer-events-none');
  }

  /**
   * Lazyly load the push image once.
   */
  loadPushImage() {
    const { push } = this.$refs;

    if (!push) {
      return;
    }

    const src = push.getAttribute('data-bg-src');

    if (src) {
      push.style.backgroundImage = `url(${src})`;
      push.removeAttribute('data-bg-src');
    }
  }

  /**
   * Close other menu when focus is set on a item.
   * @param {Number} index The item position.
   */
  onItemSecondLevelFocusin(index) {
    this.$refs.items
      .filter((otherItem, i) => i !== index && otherItem.submenuIsOpen)
      .forEach((otherItem) => otherItem.closeSubmenu());
  }

  /**
   * Proxy up the `closeSubmenu` event.
   * @param {Number} index The current submenu index.
   */
  onItemsCloseSubmenu(index) {
    this.$emit('closeSubmenu', this.$refs.items[index], index);
  }

  /**
   * Proxy up the `openSubmenu` event and sync submenus opening.
   * @param {Number} index The current submenu index.
   */
  onItemsOpenSubmenu(index) {
    this.$emit('openSubmenu', this.$refs.items[index], index);
    this.$refs.items
      .filter((otherItem, i) => i !== index && otherItem.hasSubmenu)
      .forEach((otherItem) => otherItem.closeSubmenu());
  }

  /**
   * Close the currently opened submenu and save it.
   * @return {void}
   */
  closeCurrentSubmenu() {
    const currentSubmenu = this.$refs.items.find((item) => item.submenuIsOpen);

    if (!currentSubmenu) {
      return;
    }

    currentSubmenu.closeSubmenu();
  }
}
